import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Row, Col, Card, Modal, Form, Spinner, Badge, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelFieldComponent, LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { FaPlus, FaWallet, FaFileInvoice } from "react-icons/fa";

export default function SaleCreatePage() {
    const { t } = useContext(TranslatorContext);
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);

    // Customer balance and pending sales states
    const [customerBalance, setCustomerBalance] = useState(null);
    const [showPendingSales, setShowPendingSales] = useState(false);
    const [pendingSales, setPendingSales] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedSaleForPayment, setSelectedSaleForPayment] = useState(null);
    const [paymentForm, setPaymentForm] = useState({
        payment_method_id: '',
        amount: 0,
        payment_date: getTodayDate()
    });

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Check if user has permission to edit sale date
    const canEditSaleDate = hasPermission("Sales date edit");

    // Get today's date in YYYY-MM-DD format
    function getTodayDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Customer Modal States
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerForm, setCustomerForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [customerErrors, setCustomerErrors] = useState({});
    const [customerLoading, setCustomerLoading] = useState(false);

    const [formData, setFormData] = useState({
        customer_id: null,
        sale_date: getTodayDate(),
        notes: "",
        products: [{ product_id: "", quantity: 1, sale_price: 0, discount_price: 0, extra_price: 0 }],
        payments: [{ payment_method_id: "", amount: 0, payment_date: getTodayDate() }],
    });

    // Fetch customer balance when customer is selected
    useEffect(() => {
        if (formData.customer_id) {
            fetchCustomerBalance(formData.customer_id);
        } else {
            setCustomerBalance(null);
            setPendingSales([]);
            setShowPendingSales(false);
        }
    }, [formData.customer_id]);

    const fetchCustomerBalance = async (customerId) => {
        setLoadingBalance(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-balance/${customerId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            setCustomerBalance(response.data);
        } catch (error) {
            console.error("Error fetching customer balance:", error);
            setCustomerBalance(null);
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchPendingSales = async (customerId) => {
        setLoadingBalance(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-pending-sales/${customerId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data.status) {
                setPendingSales(response.data.pending_sales);
            }
        } catch (error) {
            console.error("Error fetching pending sales:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch pending sales'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoadingBalance(false);
        }
    };

    const handleShowPendingSales = async () => {
        if (!formData.customer_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select a customer first'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (!showPendingSales) {
            await fetchPendingSales(formData.customer_id);
        }
        setShowPendingSales(!showPendingSales);
    };

    const handleMakePayment = (sale) => {
        setSelectedSaleForPayment(sale);
        setPaymentForm({
            payment_method_id: '',
            amount: sale.due,
            payment_date: getTodayDate()
        });
        setShowPaymentModal(true);
    };

    const handlePaymentSubmit = async () => {
        if (!paymentForm.payment_method_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select payment method'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (paymentForm.amount <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Amount must be greater than 0'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sale-payment`,
                {
                    customer_id: formData.customer_id,
                    amount: paymentForm.amount,
                    payment_method_id: paymentForm.payment_method_id,
                    payment_date: paymentForm.payment_date,
                    sale_id: selectedSaleForPayment?.id // Pass the sale_id to identify which sale to update
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Payment allocated successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });

                setShowPaymentModal(false);

                // Refresh balance
                await fetchCustomerBalance(formData.customer_id);

                // Refresh pending sales to get updated data
                if (showPendingSales) {
                    await fetchPendingSales(formData.customer_id);
                } else {
                    // Even if pending sales panel is closed, fetch in background to keep data fresh
                    fetchPendingSales(formData.customer_id);
                }

                // Also check if the selected sale is now fully paid and remove from local state
                if (selectedSaleForPayment && response.data.paid_sale_id) {
                    setPendingSales(prev => prev.filter(sale => sale.id !== selectedSaleForPayment.id));
                }

                if (response.data.remaining_unallocated > 0) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'info',
                        title: `${t('Remaining amount')}: ${response.data.remaining_unallocated}`,
                        showConfirmButton: false,
                        timer: 3000
                    });
                }

                // Reset selected sale
                setSelectedSaleForPayment(null);
            }
        } catch (error) {
            console.error("Error making payment:", error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.message || t('Payment failed'),
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    // Fetch data for customers, products, payment methods
    useEffect(() => {
        setSidebar(false);
        fetchInitialData();
    }, [AuthToken, setSidebar]);

    const fetchInitialData = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/sales/create`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then((res) => {
                setCustomers(res.data.customers || []);
                setProducts(res.data.products || []);
                setPaymentMethods(res.data.payment_methods || []);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    };

    // Customer Modal Handlers
    const openCustomerModal = () => {
        setCustomerForm({ name: '', email: '', phone: '', address: '', img: null });
        setCustomerErrors({});
        setShowCustomerModal(true);
    };

    const handleCustomerChange = (e) => {
        const { name, value, type, files } = e.target;
        setCustomerForm(prev => ({ ...prev, [name]: type === 'file' ? files[0] : value }));
        if (customerErrors[name]) setCustomerErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleCustomerSubmit = async (e) => {
        e.preventDefault();
        setCustomerLoading(true);
        setCustomerErrors({});

        try {
            const formData = new FormData();
            for (let key in customerForm) {
                if (customerForm[key] !== null && customerForm[key] !== '') {
                    formData.append(key, customerForm[key]);
                }
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/customers`, formData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Refresh customers list
                const customersRes = await axios.get(`${process.env.REACT_APP_API_URL}/sales/create`, {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                });
                setCustomers(customersRes.data.customers || []);

                // Auto-select the newly created customer
                const newCustomer = response.data.customer;
                if (newCustomer && newCustomer.id) {
                    setFormData(prev => ({ ...prev, customer_id: newCustomer.id }));
                }

                setShowCustomerModal(false);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Customer added successfully!'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } catch (error) {
            if (error.response?.status === 400 || error.response?.status === 422) {
                setCustomerErrors({ general: error.response.data.message });
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: error.response.data.message,
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            } else {
                console.error(error);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Failed to add customer'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } finally {
            setCustomerLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // If changing sale_date and user doesn't have permission, prevent change
        if (name === "sale_date" && !canEditSaleDate) {
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
        setSubmitError(null);
    };

    // Get available products (not already selected)
    const getAvailableProducts = (currentIndex) => {
        const selectedProductIds = formData.products
            .filter((_, index) => index !== currentIndex && _.product_id)
            .map(_ => _.product_id);

        return products.filter(product => !selectedProductIds.includes(product.product_id));
    };

    // Product change handler with stock validation
    const handleProductChange = (index, fieldName, fieldValue) => {
        const updatedProducts = [...formData.products];

        if (fieldName === "quantity") {
            const qty = parseInt(fieldValue);
            const selectedProduct = products.find(p => p.product_id === updatedProducts[index].product_id);
            const maxStock = selectedProduct ? selectedProduct.stock_qty : 0;

            if (qty > maxStock) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: `${t("Cannot add more than available stock")}: ${maxStock}`,
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
                updatedProducts[index][fieldName] = maxStock;
            } else if (qty < 1) {
                updatedProducts[index][fieldName] = 1;
            } else {
                updatedProducts[index][fieldName] = qty;
            }
        } else if (fieldName === "product_id") {
            updatedProducts[index].product_id = fieldValue;
            // Set default sale_price from product
            const selectedProduct = products.find(p => p.product_id === fieldValue);
            if (selectedProduct) {
                updatedProducts[index].sale_price = selectedProduct.sale_price || 0;
            }
        } else {
            updatedProducts[index][fieldName] = parseFloat(fieldValue) || 0;
        }

        setFormData(prev => ({ ...prev, products: updatedProducts }));
        // Clear specific product error
        if (errors[`products.${index}.${fieldName}`]) {
            setErrors(prev => ({ ...prev, [`products.${index}.${fieldName}`]: "" }));
        }
        setSubmitError(null);
    };

    // Total calculation including discount and extra (matches backend logic)
    const calculateTotal = (product) => {
        const qty = product.quantity || 0;
        const sale = product.sale_price || 0;
        const discount = product.discount_price || 0;
        const extra = product.extra_price || 0;
        return (qty * sale) - discount + extra;
    };

    const grandTotal = formData.products.reduce((acc, p) => acc + calculateTotal(p), 0);

    // Calculate remaining amount
    const getRemainingAmount = () => {
        const paidAmount = formData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
        return Math.max(0, grandTotal - paidAmount);
    };

    const handlePaymentChange = (index, fieldName, fieldValue) => {
        if (fieldName === 'payment_method_id') {
            const updatedPayments = [...formData.payments];
            updatedPayments[index][fieldName] = parseInt(fieldValue);
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
            if (errors[`payments.${index}.${fieldName}`]) {
                setErrors(prev => ({ ...prev, [`payments.${index}.${fieldName}`]: "" }));
            }
            setSubmitError(null);
            return;
        }

        if (fieldName === 'payment_date') {
            const updatedPayments = [...formData.payments];
            updatedPayments[index][fieldName] = fieldValue;
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
            setSubmitError(null);
            return;
        }

        // Handle amount change
        let newAmount = parseFloat(fieldValue);
        if (isNaN(newAmount) || newAmount < 0) newAmount = 0;

        // Calculate running total BEFORE this payment
        let runningTotalBefore = 0;
        for (let i = 0; i < index; i++) {
            runningTotalBefore += Number(formData.payments[i].amount) || 0;
        }

        // Calculate remaining amount for this payment
        const remainingForThisPayment = grandTotal - runningTotalBefore;

        // If trying to enter amount greater than remaining, set to remaining
        if (newAmount > remainingForThisPayment) {
            newAmount = remainingForThisPayment;
            if (remainingForThisPayment > 0) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: `${t("Amount cannot exceed")} ${remainingForThisPayment.toFixed(2)}. ${t("Setting to remaining amount")} ${remainingForThisPayment.toFixed(2)}`,
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                });
            }
        }

        // Update the payment
        const updatedPayments = [...formData.payments];
        updatedPayments[index] = {
            ...updatedPayments[index],
            amount: newAmount
        };

        setFormData(prev => ({ ...prev, payments: updatedPayments }));

        if (errors[`payments.${index}.amount`]) {
            setErrors(prev => ({ ...prev, [`payments.${index}.amount`]: "" }));
        }
        setSubmitError(null);
    };

    const addProductRow = () => setFormData(prev => ({
        ...prev,
        products: [...prev.products, { product_id: "", quantity: 1, sale_price: 0, discount_price: 0, extra_price: 0 }],
    }));

    const removeProductRow = (index) => {
        const updatedProducts = formData.products.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, products: updatedProducts }));
    };

    const addPaymentRow = () => {
        const remaining = getRemainingAmount();

        if (remaining <= 0) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'info',
                title: t("Sale is already fully paid"),
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, { payment_method_id: "", amount: remaining, payment_date: getTodayDate() }]
        }));
    };

    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("At least one payment row is required"),
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
            return;
        }

        const updatedPayments = formData.payments.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    // Handle amount field click - select all text
    const handleAmountClick = (e) => {
        e.target.select();
    };

    const handleSubmit = async () => {
      
        if (!formData.customer_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t('Please select a customer first'),
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }
          setSubmitting(true);
        setErrors({});
        setSubmitError(null);
        // Calculate total sale amount
        const totalSaleAmount = Number(grandTotal);
        const totalPaymentAmount = formData.payments.reduce(
            (sum, p) => sum + (Number(p.amount) || 0),
            0
        );

        // ✅ NEW: Check if any individual payment exceeds remaining amount
        let runningTotal = 0;
        for (let i = 0; i < formData.payments.length; i++) {
            const payment = formData.payments[i];
            const paymentAmount = Number(payment.amount) || 0;
            const remainingAfterPrevious = totalSaleAmount - runningTotal;

            if (paymentAmount > remainingAfterPrevious) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: t(
                        `Payment ${i + 1} amount (${paymentAmount.toFixed(2)}) exceeds remaining amount (${remainingAfterPrevious.toFixed(2)})`
                    ),
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                setSubmitting(false);
                return;
            }
            runningTotal += paymentAmount;
        }


        // ❌ CASE 2: Payment greater than grand total
        if (totalPaymentAmount > totalSaleAmount) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t(
                    `Total paid amount (${totalPaymentAmount.toFixed(2)}) cannot exceed Grand Total (${totalSaleAmount.toFixed(
                        2
                    )})`
                ),
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });

            setSubmitting(false);
            return;
        }

        // ❌ CASE 3: Payment method validation
        const invalidPayments = formData.payments.some(
            (p) => Number(p.amount) > 0 && !p.payment_method_id
        );

        if (invalidPayments) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t(
                    "Please select payment method for all payments with amount greater than 0"
                ),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });

            setSubmitting(false);
            return;
        }

        // ✅ Prepare submit data
        const submitData = {
            customer_id: formData.customer_id || null,
            sale_date: formData.sale_date,
            notes: formData.notes || "",
            products: formData.products,
            payments: formData.payments.filter(
                (p) => p.payment_method_id && Number(p.amount) > 0
            ).map(p => ({
                payment_method_id: p.payment_method_id,
                amount: p.amount,
                payment_date: p.payment_date || getTodayDate()
            })),
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sales`,
                submitData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t("Sale created successfully!"),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });

                navigate("/sales-list");
            } else {
                setSubmitError(response.data.message || "Failed to create sale");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else if (err.response?.data?.message) {
                setSubmitError(err.response.data.message);
            } else {
                console.error(err);
                setSubmitError("An error occurred while creating the sale");
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Initialize or reset payments when grand total changes
    useEffect(() => {
        if (formData.payments.length > 0 && grandTotal >= 0) {
            const totalPaid = formData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

            // If total paid doesn't match grand total, reset first payment
            if (totalPaid !== grandTotal) {
                setFormData(prev => ({
                    ...prev,
                    payments: prev.payments.map((payment, index) => {
                        if (index === 0) {
                            return { ...payment, amount: grandTotal };
                        }
                        return { ...payment, amount: 0 };
                    })
                }));
            }
        }
    }, [grandTotal]);

    const inputFullWidthStyle = { width: "100%", boxSizing: "border-box", backgroundColor: "white" };

    if (loading) return <PageLayout>{t("Loading...")}</PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mb-4 p-4">
                        <Card.Body>
                            <ButtonComponent
                                className="btn btn-secondary mb-4"
                                onClick={() => navigate("/sales-list")}
                                text={t("Back to Sales")}
                            />

                            {/* Customer Balance Display - Top Left */}
                            {customerBalance && customerBalance.total_balance > 0 && (
                                <Alert variant="warning" className="mb-4" style={{ position: 'relative' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <FaWallet size={24} />
                                            <div>
                                                <strong>{t('Customer Balance')}:</strong>{' '}
                                                {parseFloat(customerBalance.total_balance).toFixed(2)}
                                                <Badge bg="warning" className="ms-2">
                                                    {customerBalance.pending_sales?.length || 0} {t('pending sales')}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <Form.Check
                                                type="checkbox"
                                                id="show-pending-sales"
                                                label={t('Show Pending Sales')}
                                                checked={showPendingSales}
                                                onChange={handleShowPendingSales}
                                            />
                                        </div>
                                    </div>
                                </Alert>
                            )}

                            {/* Pending Sales List */}
                            {showPendingSales && pendingSales.length > 0 && (
                                <div className="mb-4">
                                    <h5><FaFileInvoice /> {t('Pending Sales')}</h5>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>{t('Sale #')}</th>
                                                    <th>{t('Date')}</th>
                                                    <th>{t('Total')}</th>
                                                    <th>{t('Paid')}</th>
                                                    <th>{t('Due')}</th>
                                                    <th>{t('Status')}</th>
                                                    <th>{t('Action')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingSales.map((sale, idx) => (
                                                    <tr key={idx}>
                                                        <td>{sale.sale_number}</td>
                                                        <td>{sale.sale_date}</td>
                                                        <td>{parseFloat(sale.total).toFixed(2)}</td>
                                                        <td>{parseFloat(sale.paid).toFixed(2)}</td>
                                                        <td className="text-danger fw-bold">
                                                            {parseFloat(sale.due).toFixed(2)}
                                                        </td>
                                                        <td>
                                                            <Badge bg={sale.status === 'partial' ? 'warning' : 'danger'}>
                                                                {sale.status === 'partial' ? t('Partial') : t('Unpaid')}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <ButtonComponent
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => handleMakePayment(sale)}
                                                                text={t('Make Payment')}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Error Display */}
                            {submitError && (
                                <div className="alert alert-danger mb-4" role="alert">
                                    {submitError}
                                </div>
                            )}

                            {/* Customer and Sale Date */}
                            <Row className="mb-4">
                                <Col xs={12} md={6}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Customer")}
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={customers.map(c => ({ value: c.id, label: c.name }))}
                                                value={customers.filter(c => c.id === formData.customer_id).map(c => ({ value: c.id, label: c.name }))}
                                                onChange={selected => {
                                                    setFormData(prev => ({ ...prev, customer_id: selected ? selected.value : "" }));
                                                    if (errors.customer_id) setErrors(prev => ({ ...prev, customer_id: "" }));
                                                }}
                                                isClearable
                                                required
                                                placeholder={t("Select Customer")}
                                                styles={{
                                                    container: provided => ({ ...provided, width: "100%" }),
                                                    control: provided => ({ ...provided, height: "38px" })
                                                }}
                                            />
                                        </div>
                                        <ButtonComponent
                                            className="btn btn-primary"
                                            onClick={openCustomerModal}
                                            style={{ height: "38px", width: "38px", padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            text={<FaPlus />}
                                        />
                                    </div>
                                    {errors.customer_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.customer_id}</span>}
                                </Col>

                                <Col xs={12} md={6}>
                                    <LabelFieldComponent
                                        type="date"
                                        label={t("Sale Date")}
                                        name="sale_date"
                                        value={formData.sale_date}
                                        onChange={handleChange}
                                        error={errors.sale_date}
                                        required
                                        disabled={!canEditSaleDate}
                                        style={{ width: "100%", backgroundColor: !canEditSaleDate ? "#e9ecef" : "transparent" }}
                                    />
                                    {!canEditSaleDate && (
                                        <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                                            {t("Sale date is set to today's date")}
                                        </small>
                                    )}
                                </Col>
                            </Row>

                            {/* Products Section */}
                            <h4 className="mb-3">{t("Products")} <span style={{ color: 'red' }}>*</span></h4>
                            {errors.products && !Array.isArray(errors.products) && (
                                <div className="alert alert-danger mb-3">{errors.products}</div>
                            )}

                            {formData.products.map((prod, index) => {
                                const availableProducts = getAvailableProducts(index);

                                return (
                                    <Card
                                        className="mb-3 p-3"
                                        key={index}
                                        style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}
                                    >
                                        <Row className="align-items-stretch">
                                            <Col xl={4}>
                                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                                                    {t("Product")} <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <Select
                                                    options={availableProducts.map(p => ({
                                                        value: p.product_id,
                                                        label: `${p.vendor_name || '-'} - ${p.product_name} `
                                                    }))}
                                                    value={products
                                                        .filter(p => p.product_id === prod.product_id)
                                                        .map(p => ({
                                                            value: p.product_id,
                                                            label: `${p.vendor_name || '-'} - ${p.product_name} `
                                                        }))[0] || null}
                                                    onChange={selected => {
                                                        handleProductChange(index, 'product_id', selected ? selected.value : "");
                                                    }}
                                                    isClearable
                                                    placeholder={t("Select Product")}
                                                    styles={{
                                                        container: provided => ({ ...provided, width: "100%" }),
                                                        control: provided => ({ ...provided, height: "38px" })
                                                    }}
                                                />
                                                {errors[`products.${index}.product_id`] && (
                                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`products.${index}.product_id`]}</span>
                                                )}
                                            </Col>

                                            <Col xl={1}>
                                                <LabelFieldComponent
                                                    type="number"
                                                    label={t("Quantity")}
                                                    name="quantity"
                                                    value={prod.quantity}
                                                    min={1}
                                                    required
                                                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                    style={inputFullWidthStyle}
                                                />
                                                {errors[`products.${index}.quantity`] && (
                                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`products.${index}.quantity`]}</span>
                                                )}
                                                {prod.product_id && products.find(p => p.product_id === prod.product_id) && (
                                                    <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: "4px" }}>
                                                        {t("Stock")}: {products.find(p => p.product_id === prod.product_id)?.stock_qty || 0}
                                                    </div>
                                                )}
                                            </Col>

                                            <Col xl={2}>
                                                <LabelFieldComponent
                                                    type="number"
                                                    label={t("Sale")}
                                                    name="sale_price"
                                                    value={prod.sale_price}
                                                    step="0.01"
                                                    min={0}
                                                    readOnly
                                                    onChange={(e) => handleProductChange(index, 'sale_price', e.target.value)}
                                                    style={inputFullWidthStyle}
                                                />
                                                {errors[`products.${index}.sale_price`] && (
                                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`products.${index}.sale_price`]}</span>
                                                )}
                                            </Col>

                                            <Col xl={1}>
                                                <LabelFieldComponent
                                                    type="number"
                                                    label={t("Discount")}
                                                    name="discount_price"
                                                    value={prod.discount_price}
                                                    onWheel={(e) => e.target.blur()}
                                                    onFocus={(e) => {
                                                        e.target.select();
                                                    }}
                                                    step="0.01"
                                                    min={0}
                                                    onChange={(e) => handleProductChange(index, 'discount_price', e.target.value)}
                                                    style={inputFullWidthStyle}
                                                />
                                                {errors[`products.${index}.discount_price`] && (
                                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`products.${index}.discount_price`]}</span>
                                                )}
                                            </Col>

                                            <Col xl={1}>
                                                <LabelFieldComponent
                                                    type="number"
                                                    label={t("Extra")}
                                                    name="extra_price"
                                                    value={prod.extra_price}
                                                    step="0.01"
                                                    min={0}
                                                    onWheel={(e) => e.target.blur()}
                                                    onFocus={(e) => {
                                                        e.target.select();
                                                    }}
                                                    onChange={(e) => handleProductChange(index, 'extra_price', e.target.value)}
                                                    style={inputFullWidthStyle}
                                                />
                                                {errors[`products.${index}.extra_price`] && (
                                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`products.${index}.extra_price`]}</span>
                                                )}
                                            </Col>
                                            <Col xl={2}>
                                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                                                    {t("Total")}
                                                </label>
                                                <input
                                                    type="number"
                                                    value={calculateTotal(prod)}
                                                    readOnly
                                                    style={{
                                                        width: "100%",
                                                        height: "38px",
                                                        padding: '6px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ced4da',
                                                        backgroundColor: '#e9ecef'
                                                    }}
                                                />
                                            </Col>

                                            <Col xl={1} className="mt-4">
                                                <ButtonComponent
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeProductRow(index)}
                                                    text="X"
                                                    disabled={formData.products.length === 1}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            })}

                            <Row className="mb-4">
                                <Col>
                                    <ButtonComponent className="btn btn-primary" onClick={addProductRow} text={t("Add Product")} />
                                </Col>
                                <Col className="text-end">
                                    <strong>
                                        <div>
                                            {t("Grand Total")}: {grandTotal}
                                        </div>
                                        <div>
                                            {t("Remaining Amount")}: {getRemainingAmount()}
                                        </div>
                                    </strong>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col xl={4}>
                                    <LabelTextareaComponent
                                        label={t("Notes")}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={4}
                                        style={{ width: "100%" }}
                                    />
                                </Col>

                                <Col xl={8}>
                                    <h5 className="mb-3">{t("Payments")}</h5>

                                    {formData.payments.map((pay, index) => (
                                        <Card key={index} className="mb-3 p-3" style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
                                            <Row className="align-items-center gx-2" style={{ margin: 0 }}>
                                                <Col xs={4} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}>
                                                            {t("Payment Method")}
                                                        </label>
                                                        <select
                                                            name="payment_method_id"
                                                            value={pay.payment_method_id}
                                                            onChange={e => handlePaymentChange(index, 'payment_method_id', e.target.value)}
                                                            style={{
                                                                width: "100%",
                                                                height: "38px",
                                                                padding: '6px 8px',
                                                                boxSizing: "border-box",
                                                                borderRadius: '4px',
                                                                border: '1px solid #ced4da',
                                                                marginBottom: 0
                                                            }}
                                                        >
                                                            <option value="">{t("Select Payment Method")}</option>

                                                            {paymentMethods
                                                                .filter(pm =>
                                                                    !formData.payments.some(
                                                                        (p, i) => p.payment_method_id == pm.id && i !== index
                                                                    )
                                                                )
                                                                .map(pm => (
                                                                    <option key={pm.id} value={pm.id}>
                                                                        {pm.name}
                                                                    </option>
                                                                ))
                                                            }
                                                        </select>
                                                        {errors[`payments.${index}.payment_method_id`] && (
                                                            <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`payments.${index}.payment_method_id`]}</span>
                                                        )}
                                                    </div>
                                                </Col>

                                                <Col xs={3} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}>
                                                            {t("Amount")}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            name="amount"
                                                            step="0.01"
                                                            min="0"
                                                            value={pay.amount}
                                                            onChange={e => handlePaymentChange(index, 'amount', e.target.value)}
                                                            onClick={handleAmountClick}
                                                            style={{ width: "100%", height: "38px", padding: '6px 8px', boxSizing: "border-box", borderRadius: '4px', border: '1px solid #ced4da', marginBottom: 0 }}
                                                        />
                                                        {errors[`payments.${index}.amount`] && (
                                                            <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`payments.${index}.amount`]}</span>
                                                        )}
                                                    </div>
                                                </Col>

                                                <Col xs={4} style={{ paddingRight: 0 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
                                                        <label style={{ fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', lineHeight: '1.2' }}>
                                                            {t("Payment Date")}
                                                        </label>
                                                        <input
                                                            type="date"
                                                            name="payment_date"
                                                            value={pay.payment_date || getTodayDate()}
                                                            onChange={e => handlePaymentChange(index, 'payment_date', e.target.value)}
                                                            style={{ width: "100%", height: "38px", padding: '6px 8px', boxSizing: "border-box", borderRadius: '4px', border: '1px solid #ced4da', marginBottom: 0 }}
                                                        />
                                                        {errors[`payments.${index}.payment_date`] && (
                                                            <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors[`payments.${index}.payment_date`]}</span>
                                                        )}
                                                    </div>
                                                </Col>

                                                <Col xs={1} className="d-flex justify-content-center align-items-center" style={{ paddingLeft: '0.5rem', paddingTop: '20px' }}>
                                                    <ButtonComponent
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => removePaymentRow(index)}
                                                        text="X"
                                                        disabled={formData.payments.length === 1}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}

                                    <ButtonComponent
                                        className="btn btn-primary btn-sm mt-1"
                                        onClick={addPaymentRow}
                                        text={t("Add Payment")}
                                        disabled={getRemainingAmount() <= 0}
                                    />
                                </Col>
                            </Row>

                            <ButtonComponent
                                className="mc-btn primary w-100 mt-3"
                                style={{ height: "45px" }}
                                onClick={handleSubmit}
                                text={submitting ? t("Creating...") : t("Create Sale")}
                                disabled={submitting}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Customer Create Modal */}
            <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="lg">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{t('Add New Customer')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCustomerSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={customerForm.name}
                                        onChange={handleCustomerChange}
                                        isInvalid={!!customerErrors.name}
                                        placeholder={t('Enter customer name')}
                                    />
                                    <Form.Control.Feedback type="invalid">{customerErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={customerForm.email}
                                        onChange={handleCustomerChange}
                                        isInvalid={!!customerErrors.email}
                                        placeholder={t('Enter email address')}
                                    />
                                    <Form.Control.Feedback type="invalid">{customerErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('phone')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={customerForm.phone}
                                        onChange={handleCustomerChange}
                                        isInvalid={!!customerErrors.phone}
                                        placeholder={t('Enter phone number')}
                                    />
                                    <Form.Control.Feedback type="invalid">{customerErrors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('image')}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        onChange={handleCustomerChange}
                                        accept="image/*"
                                        isInvalid={!!customerErrors.img}
                                    />
                                    <Form.Control.Feedback type="invalid">{customerErrors.img}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('address')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={customerForm.address}
                                onChange={handleCustomerChange}
                                isInvalid={!!customerErrors.address}
                                placeholder={t('Enter address')}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.address}</Form.Control.Feedback>
                        </Form.Group>

                        {customerErrors.general && <p className="text-danger">{customerErrors.general}</p>}
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <ButtonComponent className="btn btn-secondary" onClick={() => setShowCustomerModal(false)}>
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={customerLoading}>
                            {customerLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Payment Modal */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="md">
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>{t('Make Payment')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSaleForPayment && (
                        <>
                            <div className="mb-3">
                                <strong>{t('Sale #')}:</strong> {selectedSaleForPayment.sale_number}<br />
                                <strong>{t('Due Amount')}:</strong> <span className="text-danger fw-bold">{parseFloat(selectedSaleForPayment.due).toFixed(2)}</span>
                            </div>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Method')} <span className="text-danger">*</span></Form.Label>
                                <select
                                    className="form-control"
                                    value={paymentForm.payment_method_id}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method_id: e.target.value })}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(pm => (
                                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                                    ))}
                                </select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Amount')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="number"
                                    className="form-control"
                                    step="0.01"
                                    min="0.01"
                                    max={selectedSaleForPayment.due}
                                    value={paymentForm.amount}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                                />
                                <small className="text-muted">{t('Max amount')}: {parseFloat(selectedSaleForPayment.due).toFixed(2)}</small>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Payment Date')} <span className="text-danger">*</span></Form.Label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={paymentForm.payment_date}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-3">
                    <ButtonComponent className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                        {t('Cancel')}
                    </ButtonComponent>
                    <ButtonComponent className="btn btn-primary" onClick={handlePaymentSubmit}>
                        {t('Pay Now')}
                    </ButtonComponent>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}